import type { ReactNode } from 'react';
import type { AIconProps } from '../../atoms/AIcon/AIcon.types';
import type { MSlotProps } from '../../molecules/MSlot/MSlot.types';

export enum OTabContainerAlignment {
  Left = 'Left',
  Centered = 'Centered',
  /**
   * @deprecated Use `OTabContainerAlignment.Centered` instead.
   */
  Center = 'Center',
}

export interface OTabContainerEyebrow {
  eyebrowText?: string;
}

export interface OTabContainerHeader {
  size?: string;
  text?: string;
  headerTag?: string;
}

export interface OTabContainerBody {
  bodyText?: string;
}

export type OTabContainerIcon = Partial<Pick<AIconProps, 'size' | 'icon'>>;
export type OTabContainerBooleanFlag = string | boolean;
export type OTabContainerAemReactComponentMapping = NonNullable<MSlotProps['aemReactComponentMapping']>;

export interface OTabContainerTextBlockSimple {
  aEyebrow?: OTabContainerEyebrow;
  aHeader?: OTabContainerHeader;
  aBody?: OTabContainerBody;
}

/**
 * Fields shared by both CTA slots. Each slot locks `buttonType` to a single
 * allowed style (see `OTabContainerPrimaryButton` and `OTabContainerSecondaryButton`).
 */
export interface OTabContainerButtonBase {
  buttonText?: string;
  size?: string;
  ctaLink?: string;
  openInANewTab?: string | boolean;
}

/** Primary CTA slot (`mButton`). Renders as `Tertiary`. */
export interface OTabContainerPrimaryButton extends OTabContainerButtonBase {
  buttonType?: 'Tertiary';
}

/** Secondary CTA slot (`mButton2`). Renders as `Secondary`. */
export interface OTabContainerSecondaryButton extends OTabContainerButtonBase {
  buttonType?: 'Secondary';
}

export interface OTabContainerContentHeader {
  /** Toggles the primary CTA in `MContentHeader`. */
  cta?: OTabContainerBooleanFlag;
  /** Toggles the secondary CTA in `MContentHeader`. */
  secondaryCta?: OTabContainerBooleanFlag;
  mTextBlockSimple?: OTabContainerTextBlockSimple;
  /** Primary CTA configuration. Rendered only when `cta === 'true'`. Always renders as `Tertiary`. */
  mButton?: OTabContainerPrimaryButton;
  /** Secondary CTA configuration. Rendered only when `secondaryCta === 'true'`. Always renders as `Secondary`. */
  mButton2?: OTabContainerSecondaryButton;
}

export interface OTabContainerTabControl {
  aLabel?: { text?: string };
  /** Per-tab description label. Rendered only when the group-level `description` flag is `true`. */
  aLabel2?: { text?: string };
  /** Per-tab icon configuration for `AIcon`. Rendered only when the group-level `icon` flag is `true`. */
  aIcon?: OTabContainerIcon;
}

export interface OTabContainerTabItem {
  active?: OTabContainerBooleanFlag;
  mTabControl?: OTabContainerTabControl;
  /**
   * React content to render inside the tab panel when this tab is active.
   * Use for Storybook/demo/test scenarios, or anywhere the panel body is authored in code.
   * Ignored when `mSlot` is supplied with authorable AEM content.
   */
  content?: ReactNode;
  /**
   * AEM slot configuration for this tab's panel. When populated (`cqItems` has keys
   * or `isAuthorMode` is true) the molecule renders an `MSlot` instead of `content`.
   */
  mSlot?: Partial<MSlotProps>;
}

export interface OTabContainerTabGroup {
  /** Shows or hides per-tab icons from `mTabControl.aIcon`. */
  icon?: OTabContainerBooleanFlag;
  /** Shows or hides per-tab descriptions from `mTabControl.aLabel2.text`. */
  description?: OTabContainerBooleanFlag;
  mTabItemMulti?: OTabContainerTabItem[];
}

export interface OTabContainerProps {
  alignment?: OTabContainerAlignment;
  pagePath?: string;
  itemPath?: string;
  isAuthorMode?: boolean;
  aemReactComponentMapping?: OTabContainerAemReactComponentMapping;
  /**
   * When `true`, the organism reads the URL hash fragment after hydration to select the
   * deep-linked tab, listens for `hashchange`/`popstate` to follow browser navigation,
   * writes `#<tab-slug>` via `history.replaceState` when the user changes tabs, and
   * scrolls the organism into view on deep-link load. Defaults to `false` — the tab
   * selection is driven purely by `active: true` on the matching item (first tab as
   * fallback).
   *
   * The first paint always renders the authored `active` tab (URL hashes are not
   * visible to the server, so reading the hash during the initial render would create
   * a hydration mismatch). The hash-addressed tab is selected in a post-hydration
   * effect, so deep-link loads briefly show the authored tab before switching.
   *
   * The hash slug is derived from `mTabControl.aLabel.text` via the shared `toUrlSlug`
   * utility, so no authored tab id is required. Only one `OTabContainer` per page should
   * enable this — multiple enabled instances will compete for the single URL hash.
   */
  enableHashDeepLink?: OTabContainerBooleanFlag;
  /**
   * When `true`, all tabs share a uniform width equal to the widest tab's rendered
   * width at every breakpoint. After mount (and on every container resize), the organism
   * measures the natural rendered width of each tab button with `getBoundingClientRect`,
   * finds the maximum, and writes it as `--o-tab-equal-width` on the wrapper. The CSS in
   * `OTabContainer.styles.tsx` consumes this custom property unconditionally, so the
   * uniform width applies on mobile, tablet, and desktop alike. If the combined equal-width
   * strip overflows the container, Swiper's scroll controls remain available.
   *
   * Defaults to `false`.
   */
  equalTabWidths?: OTabContainerBooleanFlag;
  mContentHeader?: OTabContainerContentHeader;
  mTabGroup?: OTabContainerTabGroup;
}
